import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import {
  BackLink, HeroStatus, Section, SectionLabel, Card, CardTitle, KvGrid, KvItem,
  StatsRow, StatCard, Tabs, TabPanel, FooterActions, ActionButton, Alert, EmptyState,
} from "../../components/result/Primitives";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { DateInputIT } from "../../components/common/DateTimeIT";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Pause, Play, Save, Hourglass, ToggleRight,
} from "lucide-react";
import { toast } from "sonner";
import { BrandingCard } from "../../components/BrandingCard";
import { UsersManagement } from "../../components/UsersManagement";
import { JobTitlesManager } from "../../components/JobTitlesManager";

const fmtEur = (cents) => `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const PLAN_BADGE = {
  internal: "bg-subtle text-text-secondary",
  professional: "bg-accent-subtle text-accent-strong",
  business: "bg-accent-subtle text-accent-strong",
  enterprise: "bg-brand/10 text-text-primary",
};
const STATUS_BADGE = {
  active: "bg-success-subtle text-success-strong",
  suspended: "bg-danger-subtle text-danger-strong",
  trial: "bg-warning-subtle text-warning-strong",
};

export default function SuperAdminOrgDetail() {
  const { org_id } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [users, setUsers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [orgRes, usersRes, reqRes] = await Promise.all([
        api.get(`/superadmin/organizations/${org_id}`),
        api.get(`/superadmin/users`, { params: { org_id } }),
        api.get(`/superadmin/requests`, { params: { org_id, limit: 50 } }),
      ]);
      setOrg(orgRes.data);
      setUsers(usersRes.data);
      setRequests(reqRes.data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [org_id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !org) {
    return <div className="text-sm text-text-muted">Caricamento…</div>;
  }

  const toggleStatus = async () => {
    const next = org.status === "active" ? "suspended" : "active";
    try {
      await api.put(`/superadmin/organizations/${org_id}`, { status: next });
      toast.success(next === "active" ? "Organizzazione riattivata" : "Organizzazione sospesa");
      load();
    } catch (e) { toast.error("Errore aggiornamento status"); }
  };

  return (
    <div className="max-w-5xl space-y-6">
      <BackLink to="/superadmin/organizations" label="Organizzazioni" />

      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight" data-testid="org-detail-name">{org.name}</h1>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className="text-sm text-text-secondary font-mono">{org.vat_number || "P.IVA non specificata"}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${STATUS_BADGE[org.status] || "bg-subtle"}`}>
              {org.status}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${PLAN_BADGE[org.plan_id] || "bg-subtle"}`}>
              {org.plan?.display_name || org.plan_id}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {org.id !== "8energy" && (
            <Button
              variant="outline"
              onClick={toggleStatus}
              data-testid="org-toggle-status"
              className="rounded-md border-border h-10"
            >
              {org.status === "active" ? <><Pause size={14} className="mr-1.5" /> Sospendi</> : <><Play size={14} className="mr-1.5" /> Riattiva</>}
            </Button>
          )}
        </div>
      </div>

      {org.status === "suspended" && (
        <Alert variant="danger">
          Organizzazione sospesa. Gli utenti non possono inviare nuove richieste.
        </Alert>
      )}

      <Tabs
        defaultTab="overview"
        tabs={[
          { id: "overview", label: "Panoramica" },
          { id: "anagrafica", label: "Anagrafica" },
          { id: "users", label: `Utenti (${users.length})` },
          { id: "job-titles", label: "Mansioni" },
          { id: "services", label: "Moduli attivi" },
          { id: "pricing", label: "Pricing override" },
          { id: "requests", label: `Storico richieste (${requests.length})` },
          { id: "billing", label: "Fatturazione" },
          { id: "notes", label: "Note" },
        ]}
      >
        <TabPanel id="overview">
          <StatsRow columns={4}>
            <StatCard
              label="Quota mese"
              value={`${org.monthly_quota_used} / ${org.monthly_quota_limit >= 999999 ? "∞" : org.monthly_quota_limit}`}
              detail={org.monthly_quota_used > org.monthly_quota_limit ? `+${org.monthly_quota_used - org.monthly_quota_limit} extra` : "Entro quota"}
              detailColor={org.monthly_quota_used > org.monthly_quota_limit ? "danger" : "default"}
            />
            <StatCard label="MRR" value={fmtEur(org.plan?.monthly_fee_cents)} detail="Piano mensile" />
            <StatCard
              label="Margine mese"
              value={fmtEur(org.margin_month_cents)}
              detail={fmtEur(org.revenue_month_cents) + " ricavi"}
              detailColor={org.margin_month_cents >= 0 ? "success" : "danger"}
            />
            <StatCard label="Richieste totali" value={org.requests_this_month || 0} detail="Mese corrente" />
          </StatsRow>

          <Section>
            <Card>
              <CardTitle>Contratto</CardTitle>
              <KvGrid columns={2}>
                <KvItem label="Piano" value={org.plan?.display_name} />
                <KvItem label="Status" value={org.status} />
                <KvItem label="Data inizio" value={org.contract_start_date ? new Date(org.contract_start_date).toLocaleDateString("it-IT") : "—"} />
                <KvItem label="Data fine" value={org.contract_end_date ? new Date(org.contract_end_date).toLocaleDateString("it-IT") : "Aperto"} />
                <KvItem label="Setup fee" value={fmtEur(org.setup_fee_amount_cents)} />
                <KvItem label="Setup pagato" value={org.setup_fee_paid ? "Sì" : "No"} valueColor={org.setup_fee_paid ? "success" : "warning"} />
              </KvGrid>
            </Card>
          </Section>

          <Section>
            <BrandingCard orgId={org_id} />
          </Section>
        </TabPanel>

        <TabPanel id="anagrafica">
          <OrgAnagrafica org={org} onSaved={load} />
        </TabPanel>

        <TabPanel id="users">
          <UsersManagement mode="super-org" orgId={org.id} orgName={org.name} initialUsers={users} />
        </TabPanel>

        <TabPanel id="job-titles">
          <JobTitlesManager orgId={org.id} />
        </TabPanel>

        <TabPanel id="services">
          <EmptyState
            icon={ToggleRight}
            title="Moduli attivi per questa organizzazione"
            description="Attiva o disattiva i moduli (Presenze, Ticket, Formazione, Verifica…) dalla pagina «Funzioni per cliente», già filtrata su questo tenant."
            primaryAction={{
              label: "Apri Funzioni per cliente",
              onClick: () => navigate(`/superadmin/tenant-features?q=${encodeURIComponent(org.id)}`),
            }}
            testId="org-modules-link"
          />
        </TabPanel>

        <TabPanel id="pricing">
          <ComingSoon title="Pricing override per servizio"
            description="Crea pricing custom per singolo servizio (es. Visura €3,50 invece del prezzo piano €4). Implementeremo quando avrai il primo cliente con esigenze pricing differenziate." />
        </TabPanel>

        <TabPanel id="requests">
          {requests.length === 0 ? (
            <EmptyState title="Nessuna richiesta" description="Questa organizzazione non ha richieste registrate." />
          ) : (
            <div className="bg-surface border border-border rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Data</th>
                    <th className="text-left px-5 py-3 font-medium">Utente</th>
                    <th className="text-left px-5 py-3 font-medium">Servizio</th>
                    <th className="text-left px-5 py-3 font-medium">Stato</th>
                    <th className="text-left px-5 py-3 font-medium">Cache</th>
                    <th className="text-right px-5 py-3 font-medium">Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="px-5 py-3 text-xs text-text-secondary">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                      <td className="px-5 py-3 font-mono text-xs">{r.user_email}</td>
                      <td className="px-5 py-3 text-text-primary">{r.service_name}</td>
                      <td className="px-5 py-3"><span className="text-xs">{r.status}</span></td>
                      <td className="px-5 py-3">{r.cache_hit ? <span className="text-accent-strong text-xs">Sì</span> : <span className="text-text-muted text-xs">No</span>}</td>
                      <td className="px-5 py-3 text-right tabular-nums">{fmtEur(r.cost_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabPanel>

        <TabPanel id="billing">
          <ComingSoon
            title="Fatturazione"
            description="Stripe integration e generazione fatture mensili in arrivo. Per ora la fatturazione avviene via email manuale."
          />
        </TabPanel>

        <TabPanel id="notes">
          <OrgNotes org={org} onSaved={load} />
        </TabPanel>
      </Tabs>
    </div>
  );
}

const ComingSoon = ({ title, description }) => (
  <EmptyState
    icon={Hourglass}
    title={title}
    description={description}
  />
);

const OrgAnagrafica = ({ org, onSaved }) => {
  const [form, setForm] = useState({
    name: org.name || "",
    legal_name: org.legal_name || "",
    vat_number: org.vat_number || "",
    billing_email: org.billing_email || "",
    plan_id: org.plan_id || "internal",
    status: org.status || "active",
    contract_start_date: org.contract_start_date ? org.contract_start_date.slice(0, 10) : "",
    contract_end_date: org.contract_end_date ? org.contract_end_date.slice(0, 10) : "",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/organizations/${org.id}`, form);
      toast.success("Anagrafica aggiornata");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <Card>
      <CardTitle>Dati organizzazione</CardTitle>
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Nome commerciale" value={form.name} onChange={(v) => upd("name", v)} />
        <FormField label="Ragione sociale" value={form.legal_name} onChange={(v) => upd("legal_name", v)} />
        <FormField label="P.IVA" value={form.vat_number} onChange={(v) => upd("vat_number", v)} />
        <FormField label="Billing email" type="email" value={form.billing_email} onChange={(v) => upd("billing_email", v)} />
        <div>
          <Label className="text-xs font-medium text-text-secondary">Piano</Label>
          <Select value={form.plan_id} onValueChange={(v) => upd("plan_id", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="internal">Internal (8energy)</SelectItem>
              <SelectItem value="professional">Professional</SelectItem>
              <SelectItem value="business">Business</SelectItem>
              <SelectItem value="enterprise">Enterprise</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-medium text-text-secondary">Status</Label>
          <Select value={form.status} onValueChange={(v) => upd("status", v)}>
            <SelectTrigger className="mt-1.5 h-10 rounded-md border-border bg-surface"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Attiva</SelectItem>
              <SelectItem value="suspended">Sospesa</SelectItem>
              <SelectItem value="trial">Trial</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FormField label="Data inizio contratto" type="date" value={form.contract_start_date} onChange={(v) => upd("contract_start_date", v)} />
        <FormField label="Data fine contratto" type="date" value={form.contract_end_date} onChange={(v) => upd("contract_end_date", v)} />
      </div>
      <FooterActions>
        <ActionButton icon={Save} onClick={save} disabled={saving} testId="org-save-anagrafica">
          {saving ? "Salvataggio…" : "Salva modifiche"}
        </ActionButton>
      </FooterActions>
    </Card>
  );
};

const OrgNotes = ({ org, onSaved }) => {
  const [notes, setNotes] = useState(org.notes || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/organizations/${org.id}`, { notes });
      toast.success("Note salvate");
      onSaved();
    } catch (e) { toast.error("Errore"); }
    finally { setSaving(false); }
  };
  return (
    <Card>
      <CardTitle>Note commerciali</CardTitle>
      <textarea
        data-testid="org-notes-textarea"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={8}
        placeholder="Appunti, contesto commerciale, contatti chiave…"
        className="w-full px-3 py-2 text-sm rounded-md border border-border bg-surface focus-visible:ring-accent/30 focus-visible:outline-none"
      />
      <FooterActions>
        <ActionButton icon={Save} onClick={save} disabled={saving} testId="org-save-notes">
          {saving ? "Salvataggio…" : "Salva note"}
        </ActionButton>
      </FooterActions>
    </Card>
  );
};

const FormField = ({ label, value, onChange, type = "text" }) => (
  <div>
    <Label className="text-xs font-medium text-text-secondary">{label}</Label>
    {type === "date" ? (
      <DateInputIT value={value || ""} onChange={onChange} className="mt-1.5 h-10 rounded-md" />
    ) : (
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 h-10 rounded-md border-border bg-surface focus-visible:ring-accent/30 focus-visible:border-accent"
      />
    )}
  </div>
);
